import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Tooltip } from 'components/common';
import { useGet, usePost, useAuth } from 'hooks';

import DynamicsConnectModal from './DynamicsConnectModal';
import {
  getCrmDataUrl,
  getCrmDisconnectUrl,
  getCrmImage,
  getCrmDescription,
  getCrmLearnMoreUrl,
} from './helpers';
import { crmTypes } from 'utils/helpers/crm';
import { reloadCrmConnections } from 'actions/other';
import {
  ConnectionsGrid,
  ConnectionCard,
  ConnectionCardContent,
  ConnectionCardLogoContainer,
  ConnectionCardTitle,
  ConnectionCardDescription,
  ConnectionCardActions,
  ConnectionCardButtonGroup,
  LearnMoreLink,
} from './styled';

const ConnectionItem = ({ crmType, onDisconnect, onTabSwitch }) => {
  const { checkUser } = useAuth();
  const dispatch = useDispatch();
  const [{ res, loading }, fetchData] = useGet({
    url: getCrmDataUrl(crmType),
    onMount: true,
  });
  const [
    { res: disconnectData, loading: disconnectLoading },
    disconnect,
    setDisconnectRes,
  ] = usePost({
    url: getCrmDisconnectUrl(crmType),
  });
  const [showDynamicsModal, setShowDynamicsModal] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const pollRef = useRef(null);

  const handleDisconnect = () => {
    disconnect();
  };

  useEffect(() => {
    if (!disconnectData) return;

    fetchData();
    checkUser();
    dispatch(reloadCrmConnections(true));
    if (onDisconnect) {
      onDisconnect();
    }

    setDisconnectRes(null);
  }, [
    disconnectData,
    fetchData,
    checkUser,
    dispatch,
    onDisconnect,
    setDisconnectRes,
  ]);

  useEffect(() => {
    if (!connecting || !res?.crmAuthUrl) return;

    if (!pollRef.current) {
      pollRef.current = setInterval(() => {
        fetchData();
      }, 2000);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [connecting, fetchData, res?.crmAuthUrl]);

  useEffect(() => {
    if (!connecting || !res?.isAuthenticated) return;

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    checkUser();
    dispatch(reloadCrmConnections(true));
    if (onTabSwitch) {
      onTabSwitch(crmType);
    }
    setConnecting(false);
  }, [
    connecting,
    res?.isAuthenticated,
    checkUser,
    dispatch,
    onTabSwitch,
    crmType,
  ]);

  if (
    crmType === crmTypes.DYNAMICS &&
    res &&
    !res.isAuthenticated &&
    !res.crmAuthUrl
  ) {
    return (
      <>
        <ConnectionCard>
          <ConnectionCardContent>
            <ConnectionCardLogoContainer>
              <img alt={crmType + ' logo'} src={getCrmImage(crmType)} />
            </ConnectionCardLogoContainer>
            <ConnectionCardTitle>{crmType}</ConnectionCardTitle>
            <ConnectionCardDescription>
              {getCrmDescription(crmType)}
            </ConnectionCardDescription>
            <ConnectionCardActions>
              <ConnectionCardButtonGroup>
                <Button
                  type='primary'
                  onClick={() => setShowDynamicsModal(true)}
                  loading={loading}
                  block
                >
                  Connect
                </Button>
              </ConnectionCardButtonGroup>
              <LearnMoreLink
                href={getCrmLearnMoreUrl()}
                target='_blank'
                rel='noopener noreferrer'
              >
                Learn More
              </LearnMoreLink>
            </ConnectionCardActions>
          </ConnectionCardContent>
        </ConnectionCard>
        {showDynamicsModal && (
          <DynamicsConnectModal
            onClose={() => setShowDynamicsModal(false)}
            onConnected={() => {
              fetchData();
              if (onTabSwitch) {
                onTabSwitch(crmType);
              }
            }}
          />
        )}
      </>
    );
  }

  // State 2: Not authenticated but has crmAuthUrl - Show connect button
  if (res && !res.isAuthenticated && res.crmAuthUrl) {
    const openAuth = () => {
      setConnecting(true);
      window.open(res.crmAuthUrl, '_blank');
    };

    return (
      <ConnectionCard>
        <ConnectionCardContent>
          <ConnectionCardLogoContainer>
            <img alt={crmType + ' logo'} src={getCrmImage(crmType)} />
          </ConnectionCardLogoContainer>
          <ConnectionCardTitle>{crmType}</ConnectionCardTitle>
          <ConnectionCardDescription>
            {getCrmDescription(crmType)}
          </ConnectionCardDescription>
          <ConnectionCardActions>
            <ConnectionCardButtonGroup>
              <Button type='primary' onClick={openAuth} block>
                Connect
              </Button>
            </ConnectionCardButtonGroup>
            <LearnMoreLink
              href={getCrmLearnMoreUrl()}
              target='_blank'
              rel='noopener noreferrer'
            >
              Learn More
            </LearnMoreLink>
          </ConnectionCardActions>
        </ConnectionCardContent>
      </ConnectionCard>
    );
  }

  // State 3: Authenticated - Show connected state with disconnect button
  if (res && res.isAuthenticated) {
    return (
      <ConnectionCard>
        <ConnectionCardContent>
          <ConnectionCardLogoContainer>
            <img alt={crmType + ' logo'} src={getCrmImage(crmType)} />
          </ConnectionCardLogoContainer>
          <ConnectionCardTitle>{crmType}</ConnectionCardTitle>
          <ConnectionCardDescription>
            {getCrmDescription(crmType)}
          </ConnectionCardDescription>
          <ConnectionCardActions>
            <ConnectionCardButtonGroup>
              <Tooltip
                title={`This will disconnect your personal account from ${crmType}`}
              >
                <Button
                  type='danger'
                  ghost
                  onClick={handleDisconnect}
                  loading={disconnectLoading}
                  block
                >
                  Disconnect
                </Button>
              </Tooltip>
            </ConnectionCardButtonGroup>
            <LearnMoreLink
              href={getCrmLearnMoreUrl()}
              target='_blank'
              rel='noopener noreferrer'
            >
              Learn More
            </LearnMoreLink>
          </ConnectionCardActions>
        </ConnectionCardContent>
      </ConnectionCard>
    );
  }

  // Loading state
  return (
    <ConnectionCard>
      <ConnectionCardContent>
        <ConnectionCardLogoContainer>
          <img alt={crmType + ' logo'} src={getCrmImage(crmType)} />
        </ConnectionCardLogoContainer>
        <ConnectionCardTitle>{crmType}</ConnectionCardTitle>
        <ConnectionCardDescription>
          {getCrmDescription(crmType)}
        </ConnectionCardDescription>
        <ConnectionCardActions>
          <ConnectionCardButtonGroup>
            <Button type='primary' loading={true} block>
              Loading...
            </Button>
          </ConnectionCardButtonGroup>
          <LearnMoreLink
            href={getCrmLearnMoreUrl()}
            target='_blank'
            rel='noopener noreferrer'
          >
            Learn More
          </LearnMoreLink>
        </ConnectionCardActions>
      </ConnectionCardContent>
    </ConnectionCard>
  );
};

const ConnectionsOverview = ({ availableCrms, onTabSwitch, onDisconnect }) => {
  return (
    <ConnectionsGrid>
      {availableCrms.map(crmType => (
        <ConnectionItem
          key={crmType}
          crmType={crmType}
          onTabSwitch={onTabSwitch}
          onDisconnect={onDisconnect}
        />
      ))}
    </ConnectionsGrid>
  );
};

export default ConnectionsOverview;
